import React, { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export const EMPTY_CLIENT = { name: "", document: "", phone: "", whatsapp: "", email: "", address: "", number: "", complement: "", neighborhood: "", city: "", state: "", cep: "", instagram: "", notes: "" };

const onlyDigits = (v, max) => v.replace(/\D/g, "").slice(0, max);
const maskPhone = (v) => {
  const d = onlyDigits(v, 13);
  if (d.length <= 10) return d.replace(/^(\d{2})(\d{0,4})(\d{0,4}).*/, (_, a, b, c) => [a && `(${a}`, b && `) ${b}`, c && `-${c}`].filter(Boolean).join(""));
  return d.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, "($1) $2-$3");
};
const maskCep = (v) => onlyDigits(v, 8).replace(/^(\d{5})(\d{1,3})$/, "$1-$2");

export const ClientForm = ({ form, setForm }) => {
  const [cepBusy, setCepBusy] = React.useState(false);
  const lastCep = useRef("");
  const set = (k, fmt) => (e) => setForm({ ...form, [k]: fmt ? fmt(e.target.value) : e.target.value });

  useEffect(() => {
    const digits = (form.cep || "").replace(/\D/g, "");
    if (digits.length !== 8 || digits === lastCep.current) return;
    lastCep.current = digits;
    setCepBusy(true);
    fetch(`https://viacep.com.br/ws/${digits}/json/`).then((r) => r.json()).then((d) => {
      if (d.erro) { toast.error("CEP não encontrado"); return; }
      setForm((f) => ({ ...f, address: d.logradouro || f.address, neighborhood: d.bairro || f.neighborhood, city: d.localidade || f.city, state: d.uf || f.state }));
    }).catch(() => toast.error("Não foi possível buscar o CEP")).finally(() => setCepBusy(false));
  }, [form.cep]);

  return (
    <div className="space-y-3">
      <div><Label>Nome / Razão social *</Label><Input value={form.name} onChange={set("name")} className="mt-1 h-11" data-testid="client-name-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>CPF / CNPJ</Label><Input value={form.document} onChange={set("document")} inputMode="numeric" className="mt-1 h-11" data-testid="client-doc-input" /></div>
        <div><Label>Telefone</Label><Input value={form.phone} onChange={set("phone", maskPhone)} inputMode="tel" placeholder="(11) 99999-9999" className="mt-1 h-11" data-testid="client-phone-input" /></div>
        <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={set("whatsapp", (v) => onlyDigits(v, 13))} inputMode="tel" placeholder="5511999999999" className="mt-1 h-11" data-testid="client-whatsapp-input" /></div>
        <div><Label>E-mail</Label><Input value={form.email} onChange={set("email")} type="email" inputMode="email" className="mt-1 h-11" data-testid="client-email-input" /></div>
        <div className="relative"><Label>CEP</Label><Input value={form.cep} onChange={set("cep", maskCep)} inputMode="numeric" placeholder="00000-000" className="mt-1 h-11" data-testid="client-cep-input" />
          {cepBusy && <Loader2 className="absolute right-3 top-8 h-4 w-4 animate-spin text-muted-foreground" />}</div>
        <div><Label>Número</Label><Input value={form.number} onChange={set("number")} className="mt-1 h-11" data-testid="client-number-input" /></div>
        <div className="col-span-2"><Label>Endereço</Label><Input value={form.address} onChange={set("address")} className="mt-1 h-11" data-testid="client-address-input" /></div>
        <div><Label>Bairro</Label><Input value={form.neighborhood} onChange={set("neighborhood")} className="mt-1 h-11" data-testid="client-neighborhood-input" /></div>
        <div><Label>Complemento</Label><Input value={form.complement} onChange={set("complement")} className="mt-1 h-11" data-testid="client-complement-input" /></div>
        <div><Label>Cidade</Label><Input value={form.city} onChange={set("city")} className="mt-1 h-11" data-testid="client-city-input" /></div>
        <div><Label>Estado</Label><Input value={form.state} onChange={set("state", (v) => v.toUpperCase().slice(0, 2))} className="mt-1 h-11" data-testid="client-state-input" /></div>
      </div>
      <div><Label>Observações</Label><Textarea value={form.notes} onChange={set("notes")} className="mt-1" data-testid="client-notes-input" /></div>
    </div>
  );
};
